
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Download, Copy, RefreshCw, Globe, FileText, Folder, BookOpen, FileIcon } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useWebsiteSettings } from '@/hooks/useWebsiteSettings';

const AdminSitemaps = () => {
  const [generatingSitemaps, setGeneratingSitemaps] = useState<Record<string, boolean>>({});
  const [generatedXML, setGeneratedXML] = useState<Record<string, string>>({});
  const { settings } = useWebsiteSettings();

  const sitemapTypes = [
    { key: 'index', label: 'Sitemap Index', icon: Globe, description: 'Main sitemap index file' },
    { key: 'main', label: 'Main Sitemap', icon: FileText, description: 'Homepage and main pages' },
    { key: 'games', label: 'Games Sitemap', icon: Folder, description: 'All game pages' },
    { key: 'categories', label: 'Categories Sitemap', icon: Folder, description: 'All category pages' },
    { key: 'blog', label: 'Blog Sitemap', icon: BookOpen, description: 'All blog posts' },
    { key: 'pages', label: 'Pages Sitemap', icon: FileIcon, description: 'Static and custom pages' }
  ];

  const { data: games } = useQuery({
    queryKey: ['sitemap-games-count'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('games')
        .select('id', { count: 'exact' })
        .eq('hidden', false);
      
      if (error) throw error;
      return data;
    },
  });

  const { data: categories } = useQuery({
    queryKey: ['sitemap-categories-count'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('id', { count: 'exact' });
      
      if (error) throw error;
      return data;
    },
  });

  const { data: blogPosts } = useQuery({
    queryKey: ['sitemap-blog-count'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('id', { count: 'exact' })
        .eq('status', 'published');
      
      if (error) throw error;
      return data;
    },
  });

  const { data: pages } = useQuery({
    queryKey: ['sitemap-pages-count'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pages')
        .select('id', { count: 'exact' })
        .eq('published', true);
      
      if (error) throw error;
      return data;
    },
  });

  const generateSitemap = async (type: string) => {
    setGeneratingSitemaps(prev => ({ ...prev, [type]: true }));
    
    try {
      console.log(`Generating sitemap for type: ${type}`);
      
      const { data, error } = await supabase.functions.invoke('sitemap', {
        body: { type }
      });

      if (error) {
        console.error('Sitemap generation error:', error);
        throw error;
      }

      console.log(`Received sitemap data for ${type}:`, data?.substring(0, 200) + '...');
      
      setGeneratedXML(prev => ({ ...prev, [type]: data }));
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} sitemap generated successfully!`);
    } catch (error) {
      console.error('Error generating sitemap:', error);
      toast.error(`Failed to generate ${type} sitemap: ${error.message}`);
    } finally {
      setGeneratingSitemaps(prev => ({ ...prev, [type]: false }));
    }
  };

  const downloadSitemap = (type: string, xml: string) => {
    const filename = type === 'index' ? 'sitemap.xml' : `sitemap-${type}.xml`;
    const blob = new Blob([xml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`${filename} downloaded successfully!`);
  };

  const copySitemap = async (xml: string) => {
    try {
      await navigator.clipboard.writeText(xml);
      toast.success('Sitemap XML copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const generateAllSitemaps = async () => {
    for (const sitemapType of sitemapTypes) {
      await generateSitemap(sitemapType.key);
      // Add a small delay between requests
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  };

  const getItemCount = (type: string) => {
    switch (type) {
      case 'games':
        return games?.length || 0;
      case 'categories':
        return categories?.length || 0;
      case 'blog':
        return blogPosts?.length || 0;
      case 'pages':
        return (pages?.length || 0) + 4; // +4 for static pages
      case 'main':
        return 4; // Homepage, categories, blog, search
      case 'index':
        return 5; // Number of sitemap files
      default:
        return 0;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Sitemap Management</h1>
          <p className="text-gray-600 mt-2">Generate, download, and manage XML sitemaps for SEO</p>
        </div>
        <Button onClick={generateAllSitemaps} className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4" />
          Generate All
        </Button>
      </div>

      {settings?.site_url && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Site Configuration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div>
                <span className="font-medium">Base URL:</span> {settings.site_url}
              </div>
              <div className="text-sm text-gray-600">
                This URL will be used as the base for all sitemap entries
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sitemapTypes.map((sitemapType) => {
          const Icon = sitemapType.icon;
          const isGenerating = generatingSitemaps[sitemapType.key];
          const hasGenerated = generatedXML[sitemapType.key];
          const itemCount = getItemCount(sitemapType.key);

          return (
            <Card key={sitemapType.key} className="relative">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Icon className="h-5 w-5" />
                  {sitemapType.label}
                </CardTitle>
                <CardDescription>{sitemapType.description}</CardDescription>
                <Badge variant="secondary" className="w-fit">
                  {itemCount} {itemCount === 1 ? 'item' : 'items'}
                </Badge>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  onClick={() => generateSitemap(sitemapType.key)}
                  disabled={isGenerating}
                  className="w-full"
                  variant={hasGenerated ? "secondary" : "default"}
                >
                  {isGenerating ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      {hasGenerated ? 'Regenerate' : 'Generate'}
                    </>
                  )}
                </Button>

                {hasGenerated && (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Button
                        onClick={() => downloadSitemap(sitemapType.key, generatedXML[sitemapType.key])}
                        size="sm"
                        variant="outline"
                        className="flex-1"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                      <Button
                        onClick={() => copySitemap(generatedXML[sitemapType.key])}
                        size="sm"
                        variant="outline"
                        className="flex-1"
                      >
                        <Copy className="h-4 w-4 mr-1" />
                        Copy
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {Object.keys(generatedXML).length > 0 && (
        <Card className="w-full">
          <CardHeader>
            <CardTitle>Generated Sitemaps</CardTitle>
            <CardDescription>Preview and manage your generated sitemap files</CardDescription>
          </CardHeader>
          <CardContent className="w-full">
            <Tabs defaultValue={Object.keys(generatedXML)[0]} className="w-full">
              <TabsList className="w-full flex-wrap h-auto gap-1 p-1">
                {Object.keys(generatedXML).map((type) => (
                  <TabsTrigger 
                    key={type} 
                    value={type} 
                    className="capitalize flex-1 min-w-fit whitespace-nowrap"
                  >
                    {type}
                  </TabsTrigger>
                ))}
              </TabsList>
              
              <div className="mt-4">
                {Object.entries(generatedXML).map(([type, xml]) => (
                  <TabsContent key={type} value={type} className="space-y-4 mt-0">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div>
                        <h3 className="font-medium capitalize">{type} Sitemap</h3>
                        <p className="text-sm text-gray-600">
                          {xml.split('\n').length} lines, {(xml.length / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => downloadSitemap(type, xml)}
                          size="sm"
                          variant="outline"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download
                        </Button>
                        <Button
                          onClick={() => copySitemap(xml)}
                          size="sm"
                          variant="outline"
                        >
                          <Copy className="h-4 w-4 mr-1" />
                          Copy
                        </Button>
                      </div>
                    </div>
                    <Textarea
                      value={xml}
                      readOnly
                      className="font-mono text-xs min-h-96 bg-gray-50 resize-none overflow-auto"
                      placeholder="Generated XML will appear here..."
                    />
                  </TabsContent>
                ))}
              </div>
            </Tabs>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminSitemaps;
